import { Component, DoCheck, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { PdfPreviewModalComponent } from '../../../components/shared/pdf-preview-modal/pdf-preview-modal.component';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DacService } from '../dac.service';
import { SpinnerComponent } from '../../../components/shared/spinner/spinner.component';
import { StepsModule } from 'primeng/steps';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';
import { InputNumberModule } from 'primeng/inputnumber';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { fileUtils } from '@/utils/file.utils';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { DacRepository } from '@/store/dac-state/dac-state.repository';
import { getDacUpdateFormObject } from './utils/dac-edit.form-object';
import { DynamicForm, SearchableSelectOption, SelectOption } from '@/components/form-builder/form-builder.types';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { BehaviorSubject, combineLatest, forkJoin, from, of } from 'rxjs';
import { switchMap, map, finalize, catchError, take } from 'rxjs/operators';
import {
  Currency,
  DacDetails,
  DacDocsFormResponse,
  DacFormFields,
  DacListRequest,
  DacUpdateDto,
  DynaDocDetails,
  ParamGeneralField,
  User,
} from '@/types';
import { DacTransformationService } from '../dac-transformation.service';
import { form } from '@/utils/form.utils';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { DacWorkflowBlocksService, TaskVariant } from '../dac-workflow-blocks.service';
import { StatusComponent } from '@/components/shared/status/status.component';
import { url } from '@/utils/url.utils';
import { TasksStatus } from '@/types/dac-responses.types';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { DialogObject } from '@/components/dialog/dialog.types';
import { DrawerComponent } from '@/components/drawer/drawer.component';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { DacEditDynamicDocsCopyComponent } from '../dac-edit-dynamic-docs-copy/dac-edit-dynamic-docs-copy.component';
import { getDacDownloadDialog } from './utils/dac-download.dialog-object';
import { getDacUploadDialog } from './utils/dac-upload.dialog-object';
import { CurrentTasksService } from '@/pages/current-tasks/current-tasks.service';
import { getDACCommiteeDialog } from './utils/dac-committee.dialog-object';
import { CommitteeService } from '@/pages/settings/committee/committee.service';
import { CompanyService } from '@/pages/settings/company/company.service';
import _ from 'lodash';
import { dacUpdateDraftValidationSchema } from '@/validations/dac/dac.validation';
import { getDACPVItems } from '../utils/dac-pv-items';
import { DacMetaDataBlocksService } from '../dac-metadata-blocks.service';
import { getDACCommiteeMemberDialog } from './utils/dac-committee-members.dialog-object';
import { getDACCommitteeReviewDialog } from './utils/dac-committee-review.dialog-object';
import { getDacPasswordDialog } from './utils/dac-dialog.template';
import { DacGeneralParamsService } from '@/pages/settings/dac-general-params/dac-general-params.service';

@Component({
  selector: 'sm-edit-dac',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    PageHeaderComponent,
    PdfPreviewModalComponent,
    ButtonModule,
    TranslateModule,
    SpinnerComponent,
    StepsModule,
    CardModule,
    InputTextModule,
    CalendarModule,
    CheckboxModule,
    TableModule,
    InputNumberModule,
    DividerModule,
    TooltipModule,
    TagModule,
    FormBuilderComponent,
    BlockBuilderComponent,
    StatusComponent,
    DialogComponent,
    DrawerComponent,
  ],
  templateUrl: './edit-dac.component.html',
  styleUrls: ['./edit-dac.component.scss'],
})
export class EditDacComponent implements OnInit, OnDestroy, DoCheck {
  private readonly dacRepository = inject(DacRepository);
  private readonly currentTasksService = inject(CurrentTasksService);
  private readonly dacTransformationService = inject(DacTransformationService);
  private readonly dacService = inject(DacService);
  private readonly dacMetadataBlocksService = inject(DacMetaDataBlocksService);
  private readonly committeeService = inject(CommitteeService);
  private readonly companyService = inject(CompanyService);
  private readonly dacGeneralParamsService = inject(DacGeneralParamsService);
  private readonly dacWorkflowBlocksService = inject(DacWorkflowBlocksService);
  private readonly messageService = inject(MessageService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  breadcrumb = [
    { label: 'ao.plural', routerLink: ['/call-for-tender'] },
    { label: 'ao.singular', routerLink: ['edit-call-for-tender'] },
    { label: 'dac.title' },
  ];
  @ViewChild('dialog') dialogComponent!: DialogComponent;
  dacDialogObject?: DialogObject;

  @ViewChild('editDynamicDocsDrawer') editDynamicDocsDrawer!: DrawerComponent;
  editDynamicDocsDrawerObject?: DrawerObject;

  loading$ = new BehaviorSubject<boolean>(true);
  isUpdatable$ = new BehaviorSubject<boolean>(true);
  isAltered$ = new BehaviorSubject<boolean>(false);

  id: number | null = null;
  version: number | null = null;
  payant$ = new BehaviorSubject<boolean>(false);

  controlBlocks: DynamicBlocks[] = [];
  metadataBlocks: DynamicBlocks[] = [];
  isMetadataExisting = new BehaviorSubject<boolean>(false);
  dacDetails: DacDetails | null = null;

  currencyOptions: SelectOption[] = [];
  membersOptions: SelectOption[] = [];
  cpmpMembers = new BehaviorSubject<User[]>([]);
  nonBlacklistedCompanies = new BehaviorSubject<SearchableSelectOption[]>([]);
  specialCommitteeOptions: SelectOption[] = [];

  activeIndex = new BehaviorSubject<number>(1);
  onActiveIndexChange(index: number) {
    this.activeIndex.next(index);
  }

  formObject: DynamicForm = getFormBuilderObjectFactory();
  documents: DynaDocDetails[] = [];
  validationValues: Record<number, DacDocsFormResponse[]> = {};
  generalParams: BehaviorSubject<ParamGeneralField[]> = new BehaviorSubject<ParamGeneralField[]>([]);
  status = '';
  comment = new BehaviorSubject<string>('');
  commentError = new BehaviorSubject<string | null>(null);

  showPdfModal = false;
  pdfBase64 = '';
  pdfFilename = '';
  pdfUrl = new BehaviorSubject<string>('');

  ngOnInit(): void {
    this.activatedRoute.paramMap.subscribe(params => {
      const idParam = params.get('id');
      const versionParam = params.get('version');
      this.id = idParam !== null ? +idParam : null;
      this.version = versionParam !== null ? +versionParam : null;

      if (this.id && this.version) {
        this.loadDac(this.id, this.version);
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('dac.form.errors.noId'),
        });
      }
    });
  }

  ngDoCheck() {
    // const transactionalFormAltered = this.dacTransactionalFormAssignmentService.altered();
    // const submissionPiecesAltered = this.dacSubmissionPiecesServices.getValue().some(service => service.altered());

    const dto = this.dacRepository.get('dacUpdateDto');
    const initialDto = this.dacRepository.get('initialDacUpdateDto');
    const dacAltered = !_.isEqual(dto, initialDto);

    const formFieldsAltered = !_.isEqual(this.dacRepository.get('dacFormFields'), this.dacRepository.get('initialDacFormFields'));

    this.isAltered$.next(dacAltered || formFieldsAltered);
  }

  ngOnDestroy(): void {
    this.documents = [];
    this.dacRepository.reset();
  }

  buildFormObject(): void {
    this.formObject = getDacUpdateFormObject({
      store: this.dacRepository,

      dacId: this.id,
      dacVer: this.version,

      dynamicDocument: this.documents,
      validationValues: this.validationValues,

      activeIndex: this.activeIndex,
      onActiveIndexChange: (index: number) => this.onActiveIndexChange(index),

      preview: (index: number) => this.preview(index),
      pdfUrl: this.pdfUrl,

      currencyOptions: this.currencyOptions,
      nonBlacklistedCompanies: this.nonBlacklistedCompanies,
      memberOptions: this.cpmpMembers,

      isUpdatable: this.isUpdatable$,

      dateRequired: of(this.dacDetails?.dateRequired || false),
      dynamicGeneralParam: this.generalParams,
    });
  }

  onCommentChange(value: string) {
    this.comment.next(value);
    if (this.commentError.getValue()) {
      this.commentError.next(null);
    }
  }

  loadDac(id: number, version: number): void {
    this.loading$.next(true);

    forkJoin([
      this.dacService.getDacDetails(id),
      this.dacService.getDacValidationData(id, version),
      this.dacGeneralParamsService.getByDac(id, version),
      this.dacService.getMonnaies(),
      this.companyService.getNonBlacklistedCompanies(),
      this.committeeService.getAllSpecialCommittees(),
    ]).subscribe({
      next: ([dacDetails, validationData, generalParams, currenciesResponse, nonBlacklistedCompanies, specialCommittees]) => {
        this.dacDetails = dacDetails;
        this.specialCommitteeOptions = specialCommittees.map(committee => {
          return { name: committee.nom, code: committee.id };
        });
        if (this.dacDetails?.comId) {
          this.committeeService.getComMembers(this.dacDetails?.comId).subscribe(comMembers => {
            this.membersOptions = comMembers.map(member => {
              return { name: `${member.firstName} ${member.lastName}`, code: member.id };
            });
          });
        }

        //build dynamic form accordions according to doc types

        const dacInfo = dacDetails.dacDocs;

        //initialize options
        this.nonBlacklistedCompanies.next(
          nonBlacklistedCompanies.map(company => {
            const searchable = [
              company.nom,
              company.nif,
              company.address.adresse,
              company.address.codePostal,
              company.address.ville,
              company.address.pays.code,
              company.address.pays.description,
            ].join('_');

            return {
              name: `${company.nom}-${company.nif}-${company.address.pays.code}`,
              code: company.id,
              searchable,
            };
          })
        );
        this.currencyOptions = (currenciesResponse || []).map((currency: Currency) => ({
          name: currency.description,
          code: currency.id,
        }));

        this.committeeService.getCpmpMembers(dacDetails.dacDocs.dacId).subscribe(members => {
          this.cpmpMembers.next(members);
        });

        //breadcrumb && status
        this.status = dacInfo.status;
        this.breadcrumb[1].label = dacInfo.aoReference;
        this.breadcrumb[1].routerLink = ['/edit-call-for-tender', dacInfo.aoId.toString()];
        this.breadcrumb[2].label = dacInfo.reference;

        //initialize dto
        this.dacRepository.setDacUpdateDto(dacDetails.dacDocs, generalParams);

        //initialize form
        this.documents = dacInfo.dynaDocs;
        this.validationValues = this.documents.reduce((acc: Record<number, DacDocsFormResponse[]>, doc) => {
          acc[doc.id] = validationData.filter(d => d.dynaDocId == doc.id) || [];
          return acc;
        }, {});
        this.dacTransformationService.initializeForm(dacInfo.dacId, this.documents, this.validationValues).subscribe(() => {
          const payantValue = Boolean(dacInfo?.payant);

          this.dacRepository.set('dacInfo', dacInfo);
          this.isUpdatable$.next(!!dacDetails.isUpdatable || dacDetails.dacDocs.status === TasksStatus.BROUILLON);

          // Initialize payant$ for template visibility
          this.payant$.next(payantValue);

          this.buildFormObject();

          const metadataBlocksResult = this.dacMetadataBlocksService.getMetadataBlocks({
            pvItems: getDACPVItems(dacInfo),
            dacOpinions: dacDetails.avis,
          });
          this.metadataBlocks = metadataBlocksResult.blocks;
          this.isMetadataExisting.next(!metadataBlocksResult.isEmpty);

          this.controlBlocks =
            !dacDetails.processInstanceId && this.status === TasksStatus.BROUILLON
              ? this.dacWorkflowBlocksService.getDraftDacBlocks({
                  save: () => this.onSubmit('save'),
                  // Document steps now start at value 1 (no more leading "Paramètres généraux"
                  // step), so the last document step is at value `documents.length` and the
                  // final "Résumé" step is `documents.length + 1` - validate/save stay disabled
                  // until the user has stepped past every document.
                  disabledSave: combineLatest([
                    this.isAltered$.pipe(map(a => !a)),
                    this.activeIndex.pipe(map(index => index < this.documents.length)),
                  ]).pipe(map(([a, b]) => a || b)),
                  disableValidate: this.activeIndex.pipe(map(index => index < this.documents.length)),
                  globalPreview: () => this.onSubmit('preview'),
                  openEditDynamicDocsCopySheet: () => this.openEditDynamicDocsCopySheet(),
                  validate: () => this.startWorkflow(),
                })
              : this.dacWorkflowBlocksService.getFlowableDacBlocks({
                  refreshCount: dacInfo.nbrActualisation || 0,
                  task: dacDetails,
                  save: () => this.onSubmit('update'),
                  saveDates: () => this.saveDates(),
                  validate: (value = 'true') => this.nextStep(dacDetails.taskId, value),
                  reject: () => this.nextStep(dacDetails.taskId, 'false'),
                  specialValidate: (value = 'true') => this.specialValidate(dacDetails, value),
                  openDownloadAaoDialog: () => this.validateFileAffiliatedStep('downloadAAO'),
                  downloadModelDac: () => this.validateFileAffiliatedStep('downloadDAC'),
                  openUploadDialog: () => this.validateFileAffiliatedStep('upload'),
                  comment: this.comment,
                  onCommentChange: (value: string) => this.onCommentChange(value),
                  error: this.commentError,
                  accessHistory: () => this.router.navigate(['/dac-task-history', dacDetails.processInstanceId]),
                  globalPreview: () => this.onSubmit('preview'),
                  openEditDynamicDocsCopySheet: this.isUpdatable$.getValue() ? () => this.openEditDynamicDocsCopySheet() : undefined,
                });

          //render pdf if not updatable
          if (this.id && this.version && !dacDetails.isUpdatable && this.status !== TasksStatus.BROUILLON)
            this.dacService.generateListPdf(this.getDacListRequest(this.id, this.version)).subscribe({
              next: (pdfBlob: Blob) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  this.pdfBase64 = (reader.result as string).split(',')[1];
                  this.pdfFilename = `DAC-${this.id}-${this.version}.pdf`;
                };
                reader.readAsDataURL(pdfBlob);
                this.pdfUrl.next(URL.createObjectURL(pdfBlob));
                this.loading$.next(false);
              },
              error: error => {
                this.loading$.next(false);
                this.messageService.add({
                  severity: 'error',
                  summary: 'Erreur',
                  detail: error.error.message,
                });
              },
            });

          this.loading$.next(false);
        });
      },
      error: error => {
        this.loading$.next(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error.message,
        });
      },
    });
  }

  //******************************************************************************* */

  onSubmit(mode: 'save' | 'update' | 'preview'): void {
    if (['save', 'update'].includes(mode) && !this.dacRepository.get<boolean>('dacHasRightToSubmit')) {
      // Jump to the "Résumé" step, now at (document count + 1) since the leading
      // "Paramètres généraux" step was removed.
      this.onActiveIndexChange((this.dacDetails?.dacDocs.dynaDocs.length || 0) + 1);
      this.dacRepository.set('dacHasRightToSubmitTouched', true);
      form.scrollToInvalidElement();
      return;
    }

    if (!this.id || !this.version) return;

    if (mode === 'preview') {
      this.loading$.next(true);
      this.dacService
        .generateListPdf(this.getDacListRequest(this.id, this.version))
        .pipe(
          switchMap((pdfBlob: Blob) =>
            from(this.readBlobAsBase64(pdfBlob)).pipe(
              map(base64 => {
                this.pdfBase64 = base64.split(',')[1];
                this.pdfFilename = `DAC-${this.id}-${this.version}.pdf`;
                this.showPdfModal = true;
                return null;
              })
            )
          ),
          finalize(() => this.loading$.next(false)),
          catchError(() => {
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: this.translate.instant('dac.form.errors.pdfGeneration'),
            });
            return of(null);
          })
        )
        .subscribe();
      return;
    }

    const dto: DacUpdateDto = this.dacRepository.get<DacUpdateDto>('dacUpdateDto');
    dto.dacDocsRequests = this.dacRepository.get('dacFormFields');
    // dto.dacDynaFormRequest = this.dacTransactionalFormAssignmentService
    //   .getAllTransactionalFormsDtos()
    //   .map(f => ({ ...f, dacId: this.id!, display: true }));
    // dto.dacDocSoumissionCreateRequests = this.dacSubmissionPiecesServices
    //   .getValue()
    //   .flatMap(service => service.getAllSubmissionPiecesDtos())
    //   .map(f => ({ ...f, dacId: this.id! }));
    dto.parametrageGeneralDacValuesRequests = this.dacRepository.getNested('dacUpdateDto', 'parametrageGeneralDacValuesRequests') ?? [];
    dto.dacId = this.id!;

    const result = dacUpdateDraftValidationSchema().safeParse(dto);
    if (result.success) {
      this.loading$.next(true);
      (mode === 'update' ? this.dacService.updateDacDocs(this.id!, dto) : this.dacService.submitForms(dto))
        .pipe(
          switchMap(() => {
            const uploadTasks = this.documents
              .filter(doc => doc.isStatic)
              .map(doc => {
                const formData = new FormData();
                if (this.id) formData.append('dacId', this.id.toString());
                formData.append('dynaDocsId', doc.id.toString());

                return this.dacRepository.getObservableStaticFile(doc.id).pipe(
                  take(1),
                  switchMap(file => {
                    if (file) formData.append('file', file);
                    return this.dacService.uploadDacStaticFile(formData);
                  }),
                  catchError(err => {
                    this.messageService.add({
                      severity: 'error',
                      summary: 'Erreur',
                      detail: err.error.message,
                    });
                    return of(null);
                  })
                );
              });

            return uploadTasks.length ? forkJoin(uploadTasks) : of(null);
          }),
          switchMap(() => {
            if (mode === 'update') return of(null);
            return this.dacService.generateListPdf(this.getDacListRequest(this.id!, this.version!));
          }),
          switchMap((pdfBlob: Blob | null) => {
            // Handle save vs preview
            if (mode === 'save' || mode === 'update') {
              this.messageService.add({
                severity: 'success',
                summary: 'Succès',
                detail: this.translate.instant('dac.form.success.update'),
              });
              url.reloadComponent(this.router);
              return of(null);
            } else {
              return from(this.readBlobAsBase64(pdfBlob!)).pipe(
                map(base64 => {
                  this.pdfBase64 = base64.split(',')[1];
                  this.pdfFilename = `DAC-${this.id}-${this.version}.pdf`;
                  this.showPdfModal = true;
                  return null;
                })
              );
            }
          }),

          finalize(() => this.loading$.next(false)),
          catchError(() => {
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: this.translate.instant(mode === 'save' ? 'dac.form.errors.submit' : 'dac.form.errors.pdfGeneration'),
            });
            return of(null);
          })
        )
        .subscribe(() => {
          if (mode === 'save') {
            this.messageService.add({
              severity: 'success',
              summary: 'Succès',
              detail: this.translate.instant('dac.form.success.submit'),
            });
          }
        });
    } else {
      this.dacRepository.setErrors(result.error.flatten().fieldErrors);
      form.scrollToInvalidElement();
    }
  }

  private readBlobAsBase64(blob: Blob): Promise<string> {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  }

  private getDacListRequest(dacId: number, dacVer: number, dynaDocsId?: number, dynaDocsVer?: number): DacListRequest {
    let dacDocsRequests: DacFormFields[] = this.dacRepository.get<DacFormFields[]>('dacFormFields') ?? [];
    if (dynaDocsId) {
      dacDocsRequests = dacDocsRequests.filter((req: DacFormFields) => req.dynaDocsId === dynaDocsId);
    }
    return {
      dacId,
      dacVer,
      dynaDocsId,
      dynaDocsVer,
      dacDocsRequests,
      parametrageGeneralDacValuesRequests: this.dacRepository.getNested('dacUpdateDto', 'parametrageGeneralDacValuesRequests') ?? [],
    };
  }

  preview(index: number): void {
    if (!this.id || !this.version) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: this.translate.instant('dac.form.errors.missingInfo'),
      });
      return;
    }
    const currentDocument = this.documents[index - 1];
    this.loading$.next(true);

    this.dacService
      .previewListPdf(this.getDacListRequest(this.id!, this.version!, currentDocument.id, currentDocument.version))
      .pipe(
        finalize(() => this.loading$.next(false)),
        catchError(() => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('dac.form.errors.preview'),
          });
          return of(null);
        })
      )
      .subscribe(pdfResponse => {
        if (pdfResponse) {
          this.pdfBase64 = pdfResponse.pdfBase64;
          this.pdfFilename = pdfResponse.filename;
          this.showPdfModal = true;
        }
      });
  }

  openEditDynamicDocsCopySheet(): void {
    this.editDynamicDocsDrawerObject = {
      title: 'dac.dacDynamicDocCopy.title',
      description: 'dac.dacDynamicDocCopy.description',
      width: '50vw',
      dismissable: true,
      closeOnEscape: true,
      component: {
        outlet: DacEditDynamicDocsCopyComponent,
        props: {
          dacId: this.id,
          dacVer: this.version,
        },
      },
    };
    this.editDynamicDocsDrawer.toggleVisible();
  }

  startWorkflow() {
    if (!this.dacRepository.get<boolean>('dacHasRightToSubmit')) {
      this.dacRepository.set('dacHasRightToSubmitTouched', true);
      form.scrollToInvalidElement();
      return;
    }

    if (this.id) {
      if (!this.dacDetails?.dacDocs.scenarioId) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Scénario manquant',
          detail: 'Veuillez choisir un scénario dans la configuration du DAC avant de passer pour validation',
        });
        return;
      }
      this.dacService.startWorkflow(this.id).subscribe({
        next: () => {
          this.dacRepository.reset();
          url.reloadComponent(this.router);
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'Le DAC a été passé pour validation avec succès',
          });
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message,
          });
        },
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Veuillez sélectionner un DAC',
      });
    }
  }

  validateFileAffiliatedStep(
    variant: 'downloadAAO' | 'downloadDAC' | 'upload',
    options: { dateRequired?: boolean; passwordRequired?: boolean } = { dateRequired: false }
  ) {
    if (variant === 'downloadDAC') {
      this.dacDialogObject = getDacDownloadDialog({
        store: this.dacRepository,
        validate: () => {
          const type = this.dacRepository.getNested<SelectOption>('download.formatSelected').code as string;
          this.dacService.generateDacPdf(this.id!, type).subscribe((blob: Blob) => {
            fileUtils.triggerBlobDownload(blob, `${this.dacDetails?.dacDocs.reference}-DAC.${type == 'docx' ? 'zip' : 'pdf'}`);
          });
          this.dacRepository.setNested('download.formatSelected', { name: 'PDF', code: 'pdf' });
          this.dialogComponent.toggleVisible();
        },
      });
      this.dialogComponent.toggleVisible();
    } else if (variant === 'downloadAAO') {
      this.dacDialogObject = getDacDownloadDialog({
        store: this.dacRepository,
        validate: () => {
          const type = this.dacRepository.getNested<SelectOption>('download.formatSelected').code as string;
          this.dacService
            .generateAaoPdf({
              dacId: this.id!,
              dacVersion: this.version!,
              signataire: '',
              fonction: '',
              generateDocx: type === 'docx',
            })
            .subscribe((blob: Blob) => {
              fileUtils.triggerBlobDownload(blob, `${this.dacDetails?.dacDocs.reference}-AAO.${type == 'docx' ? 'zip' : 'pdf'}`);
            });
          this.dacRepository.setNested('download.formatSelected', { name: 'PDF', code: 'pdf' });
          this.dialogComponent.toggleVisible();
        },
      });
      this.dialogComponent.toggleVisible();
    } else {
      const formData = new FormData();

      // if (options.dateRequired) {
      //   const dateDate = this.validateDateConfirmationStep();
      //   if (!dateDate) return;
      //   formData.append('dateLimiteSoumission', dateDate.dateLimiteSoumission.toISOString());
      //   formData.append('dateLimiteRecptionDemande', dateDate.dateLimiteRecptionDemande.toISOString());
      // }

      this.dacDialogObject = getDacUploadDialog({
        store: this.dacRepository,
        passwordRequired: !!this.dacDetails?.passwordRequired,
        validate: () => {
          const modelDacFileAsArray = this.dacRepository.getNested<File[]>('upload', 'modelDacFile');
          const dacFileAsArray = this.dacRepository.getNested<File[]>('upload', 'dacFile');
          if (modelDacFileAsArray.length === 0) {
            this.messageService.add({
              severity: 'error',
              summary: 'Fichier manquant',
              detail: 'Veuillez sélectionner un fichier avant de passer pour validation',
            });
            return;
          }
          const modelDacFile = modelDacFileAsArray[0];
          const dacFile = dacFileAsArray[0];
          formData.append('validate', 'true');
          formData.append('comment', this.comment.getValue());
          formData.append('modelDacFile', modelDacFile);
          formData.append('dacFile', dacFile);

          if (options.passwordRequired) {
            const password = this.dacRepository.getNested<string>('upload.password');
            formData.append('password', password);
          }

          if (this.dacDetails?.taskId) {
            this.dacService.uploadDacModel(this.dacDetails?.taskId, formData).subscribe({
              next: () => {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Succès',
                  detail: 'Le DAC a été passé pour validation avec succès',
                });
                url.reloadComponent(this.router);
                this.dialogComponent.toggleVisible();
              },
              error: error => {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Erreur',
                  detail: error.error.message,
                });
              },
            });
          }
        },
      });
      this.dialogComponent.toggleVisible();
    }
  }

  nextStep(taskId?: string, value?: string) {
    const commentValue = this.comment.getValue();

    if ((value === 'false' || !value) && !commentValue) {
      this.commentError.next('Veuillez saisir un commentaire');
      return;
    }

    const formData = value ? this.dacRepository.getUpdatePayload(this.id!) : new FormData();
    const committee = this.dacRepository.get<SelectOption>('comSelected');
    if (committee.code) {
      formData.append('comId', committee?.code?.toString() || '');
      this.dacRepository.set('comSelected', undefined);
    }

    if (value != undefined) formData.append('validate', value.toString());
    formData.append('comment', commentValue);

    if (this.dacRepository.getNested('dto', 'assigneeUserId'))
      formData.append('assigneeUserId', this.dacRepository.getNested('dto', 'assigneeUserId'));

    this.loading$.next(true);

    this.currentTasksService.validateDac(Number(taskId), formData).subscribe({
      next: () => {
        this.comment.next('');
        url.reloadComponent(this.router);
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: "Le DAC a été passé pour l'étape suivante",
        });
        this.loading$.next(false);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || "Une erreur est survenue lors de la passage du DAC l'étape suivante",
        });
        this.loading$.next(false);
      },
    });
  }

  validateReporterStep(taskId: string) {
    const formData = new FormData();
    formData.append('avis', this.dacRepository.getNested<string>('review', 'comment'));
    const file = this.dacRepository.getNested<File[]>('review', 'files')[0];
    if (file) formData.append('noteFile', file);

    this.dacService.validateReporterReview(taskId, formData).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'L’avis du rapporteur a été soumis avec succès',
        });
        this.dialogComponent.toggleVisible();
        url.reloadComponent(this.router);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || "Une erreur est survenue lors de la soumission de l'avis du rapporteur",
        });
      },
    });
  }

  specialValidate(dacDetails: DacDetails | null, value?: string) {
    if (dacDetails?.dacDocs.status == TasksStatus.EN_TRAITEMENT && dacDetails.taskName == TaskVariant.AFFECTAION_COMMISSION_PERMANENTE) {
      this.dacDialogObject = getDACCommiteeDialog({
        store: this.dacRepository,
        validate: () => this.nextStep(dacDetails.taskId, value),
        committees: this.specialCommitteeOptions,
      });
      this.dialogComponent.toggleVisible();
    } else if (dacDetails?.taskName == TaskVariant.AFFECTATION_COMMISSION_SPECIALISEE) {
      this.dacDialogObject = getDACCommiteeMemberDialog({
        store: this.dacRepository,
        validate: () => this.nextStep(dacDetails.taskId, value),
        members: this.membersOptions,
      });
      this.dialogComponent.toggleVisible();
    } else if (dacDetails?.taskName == TaskVariant.AVIS_NOTE_RAPPORTEUR_COMMISSION_SPECIALISEE) {
      this.dacDialogObject = getDACCommitteeReviewDialog({
        store: this.dacRepository,
        validate: () => this.validateReporterStep(dacDetails.taskId),
      });
      this.dialogComponent.toggleVisible();
    } else if (dacDetails?.taskName == TaskVariant.CONFIRMATION_DES_DATES) {
      this.validateFileAffiliatedStep('upload', {
        dateRequired: true,
        passwordRequired: dacDetails?.passwordRequired || false,
      });
    } else if (dacDetails?.taskName == TaskVariant.PUBLICATION) {
      this.validatePublishStep(dacDetails.taskId);
    } else {
      this.nextStep(dacDetails?.taskId, value);
    }
  }

  validatePublishStep(taskId: string) {
    const passwordRequired = !!this.dacDetails?.passwordRequired;

    if (passwordRequired) {
      // Show password dialog
      this.dacDialogObject = getDacPasswordDialog({
        store: this.dacRepository,
        passwordRequired: true,
        dialogTitle: 'Authentification requise',
        specialValidate: (password: string) => this.executePublish(taskId, password),
      })!;
      this.dialogComponent.toggleVisible();
    } else {
      // No password needed - publish directly
      this.executePublish(taskId);
    }
  }

  private executePublish(taskId: string, password?: string) {
    const formData = new FormData();
    formData.append('validate', 'true');
    formData.append('comment', this.comment.getValue());

    if (password) {
      formData.append('password', password);
    }

    this.loading$.next(true);

    this.currentTasksService.validateDac(Number(taskId), formData).subscribe({
      next: () => {
        this.comment.next('');
        // Clear password from store and close dialog if it was open
        if (password) {
          this.dacRepository.setNested('passwordDialog', 'password', '');
          this.dialogComponent.toggleVisible();
        }
        url.reloadComponent(this.router);
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'Le DAC a été publié avec succès',
        });
        this.loading$.next(false);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || 'Une erreur est survenue lors de la publication du DAC',
        });
        this.loading$.next(false);
      },
    });
  }

  saveDates() {
    const dateDebutDepot = this.dacRepository.getNested<Date>('dacUpdateDto', 'dateDebutDepot');
    const dateLimiteSoumission = this.dacRepository.getNested<Date>('dacUpdateDto', 'dateLimiteSoumission');
    const dateLimiteRecptionDemande = this.dacRepository.getNested<Date>('dacUpdateDto', 'dateLimiteRecptionDemande');
    const dateOuverture = this.dacRepository.getNested<Date>('dacUpdateDto', 'dateOuverture');

    let validatedDates = true;
    if (!dateDebutDepot) {
      this.dacRepository.setNested('errors.dateDebutDepot', 'Veuillez sélectionner une date de début de dépôt');
      validatedDates = false;
    }
    if (!dateLimiteSoumission) {
      this.dacRepository.setNested('errors.dateLimiteSoumission', 'Veuillez sélectionner une date limite de soumission');
      validatedDates = false;
    }
    if (!dateLimiteRecptionDemande) {
      this.dacRepository.setNested(
        'errors.dateLimiteRecptionDemande',
        'Veuillez sélectionner une date limite de réception des demandes de clarification'
      );
      validatedDates = false;
    }
    if (!dateOuverture) {
      this.dacRepository.setNested('errors.dateOuverture', "Veuillez sélectionner une date d'ouverture");
      validatedDates = false;
    }
    if (!validatedDates) return;

    this.dacService
      .updateDacDates(this.id!, {
        dateDebutDepot: dateDebutDepot!.toISOString(),
        dateLimiteSoumission: dateLimiteSoumission!.toISOString(),
        dateLimiteRecptionDemande: dateLimiteRecptionDemande!.toISOString(),
        dateOuverture: dateOuverture!.toISOString(),
      })
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'Les dates ont été mises à jour avec succès',
          });
          url.reloadComponent(this.router);
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || 'Une erreur est survenue lors de la mise à jour des dates',
          });
        },
      });
  }
}
